import AssignmentRounded from "@mui/icons-material/AssignmentRounded";
import CalendarMonthRounded from "@mui/icons-material/CalendarMonthRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import DirectionsWalkRounded from "@mui/icons-material/DirectionsWalkRounded";
import FavoriteRounded from "@mui/icons-material/FavoriteRounded";
import MedicationRounded from "@mui/icons-material/MedicationRounded";
import ReportProblemRounded from "@mui/icons-material/ReportProblemRounded";
import RestaurantRounded from "@mui/icons-material/RestaurantRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import TrendingDownRounded from "@mui/icons-material/TrendingDownRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";

const STEP_ICONS = {
  calendar: CalendarMonthRounded,
  nutrition: RestaurantRounded,
  walk: DirectionsWalkRounded,
  medication: MedicationRounded,
  heart: FavoriteRounded,
  labs: DescriptionRounded
};

const ALERT_STYLES = {
  critical: {
    wrap: "border-l-4 border-l-red-500 bg-red-50/40 ring-red-200/30",
    iconWrap: "bg-red-100 text-red-600",
    title: "text-red-800",
    pill: "bg-red-100/80 text-red-700"
  },
  warning: {
    wrap: "border-l-4 border-l-amber-400 bg-amber-50/50 ring-amber-200/40",
    iconWrap: "bg-amber-100 text-amber-700",
    title: "text-amber-900",
    pill: "bg-amber-100/80 text-amber-800"
  },
  mild: {
    wrap: "border-l-4 border-l-blue-400 bg-blue-50/50 ring-blue-200/40",
    iconWrap: "bg-blue-100 text-blue-700",
    title: "text-blue-900",
    pill: "bg-blue-100/80 text-blue-800"
  }
};

function MetricPill({ label, value, styles }) {
  return (
    <div className={`flex flex-col rounded-lg px-2.5 py-1.5 ${styles.pill}`}>
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
      <span className="text-xs font-bold">{value}</span>
    </div>
  );
}

export default function PersonalizedRecommendations({ suggestions }) {
  if (!suggestions) return null;

  const { alerts = [], action_plan_30_days = [] } = suggestions;

  return (
    <section className="min-w-0 space-y-8">
      <header>
        <h2 className="text-lg font-semibold tracking-tight text-slate-900 md:text-xl">
          Personalized AI Recommendations
        </h2>
        <p className="mt-1 max-w-3xl text-sm leading-relaxed text-slate-500">
          Advanced clinical analysis based on your latest lab results and detected trends.
        </p>
      </header>

      {/* Alerts Grid */}
      <div className="flex flex-col gap-4">
        {alerts.map((alert, idx) => {
          const styles = ALERT_STYLES[alert.type] || ALERT_STYLES.warning;
          const Icon = alert.type === "critical" ? ReportProblemRounded : WarningAmberRounded;
          const m = alert.metrics || {};

          return (
            <div
              key={idx}
              className={`flex flex-col gap-4 rounded-2xl border border-slate-200/80 p-5 shadow-sm ring-1 ring-inset md:flex-row md:items-start ${styles.wrap}`}
            >
              <div
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm ${styles.iconWrap}`}
              >
                <Icon sx={{ fontSize: 26 }} />
              </div>
              
              <div className="min-w-0 flex-1">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className={`text-base font-bold ${styles.title}`}>{alert.title}</h3>
                  <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                    <HealthAndSafetyRounded sx={{ fontSize: 14 }} />
                    Contact: {alert.doctor}
                  </div>
                </div>
                
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{alert.description}</p>
                
                {/* Metric breakdown */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <MetricPill label="Current" value={m.current} styles={styles} />
                  <MetricPill label="Normal" value={m.normal_range} styles={styles} />
                  <MetricPill label="Deviation" value={m.deviation} styles={styles} />
                  <MetricPill label="Impact" value={m.percentage} styles={styles} />
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-slate-200/60 pt-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <CalendarMonthRounded sx={{ fontSize: 16 }} />
                    Action required {alert.timeline}
                  </div>
                  <button className="text-xs font-bold text-blue-600 hover:underline">
                    View specialist map →
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 30-Day Action Plan */}
      <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-200/60 md:p-8">
        <div className="flex items-start gap-4 border-b border-slate-100 pb-6">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-200">
            <AssignmentRounded sx={{ fontSize: 26 }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Your 30-Day Recovery Roadmap</h3>
            <p className="mt-1 text-sm text-slate-500">A structured clinical plan to stabilize and improve your markers.</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {action_plan_30_days.map((stage, idx) => (
            <div key={idx} className="relative">
              {/* Connector line for desktop */}
              {idx < 3 && (
                <div className="absolute top-6 left-1/2 hidden h-[2px] w-full bg-slate-100 lg:block" />
              )}
              
              <div className="relative flex flex-col items-center text-center lg:items-start lg:text-left">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-base font-bold text-indigo-600 ring-4 ring-white shadow-sm transition-transform hover:scale-110">
                  {idx + 1}
                </div>
                
                <div className="mt-4">
                  <span className="inline-block rounded-full bg-indigo-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700">
                    {stage.day_range}
                  </span>
                  <h4 className="mt-2 text-sm font-bold text-slate-900 leading-snug">{stage.title}</h4>
                  <ul className="mt-3 space-y-2.5">
                    {stage.steps.map((step, sIdx) => (
                      <li key={sIdx} className="flex gap-2 text-xs leading-relaxed text-slate-600">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-indigo-400" />
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
